import type React from 'react';
import { type ComponentPropsWithoutRef, forwardRef, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { RowConfig } from '../../config/types';
import { textareaMaxLength } from '../../constants';
import { containsReservedCharacters, getInputRules, validateField } from '../../utils/form-utils';

type CSCTableData = {
  headers: string[];
  rows: RowConfig[];
};

const DefaultTableData: CSCTableData = {
  rows: [],
  headers: [],
};

type Props = {
  tableData?: CSCTableData;
  ariaLabelledby?: string;
  id?: string;
  disabled?: boolean;
  title?: string;
  name?: string;
  defaultValue?: string;
  rules?: object; // Type depends on validation rules library
  errorMessages?: object; // Type depends on error handling implementation
  detailsHeading?: string;
  detailsContent?: string;
} & ComponentPropsWithoutRef<'input'>; // Allow native div props

const RowsWithInputs = (props: Props) => {
  const {
    name = 'dummyName',
    title = 'dummyTitle',
    rules,
    disabled,
    defaultValue,
    tableData = DefaultTableData, // Use DefaultTableData if tableData is not provided
    errorMessages,
    detailsHeading,
    detailsContent,
  } = props;

  const inputRules = useMemo(
    () => ({ ...getInputRules(rules ?? {}, title, textareaMaxLength) }),
    [rules, title],
  );

  // Initialize state for inputs
  const [formData, setFormData] = useState(() => {
    const initialData: Record<string, string> = {};
    tableData.rows.forEach((row) => {
      row.inputs.forEach((input: { name: string | number }) => {
        initialData[input.name] = defaultValue || ''; // Initialize with defaultValue if provided
      });
    });
    return initialData;
  });

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value, // Update the specific field based on name
    }));
  };

  const { register, getFieldState, getValues, setValue } = useFormContext();

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue ?? '');
    }
  }, [defaultValue, getValues, name, setValue]);

  const hooksFormRegister = register(name, {
    validate: (value: string) => validateField(title, inputRules, value, errorMessages),
  });

  return (
    <div>
      {title && <h2>{title}</h2>}
      {detailsHeading && detailsContent && (
        <details>
          <summary>{detailsHeading}</summary>
          <p>{detailsContent}</p>
        </details>
      )}
      <table>
        <thead>
          <tr>
            {tableData.headers.map((header, headerIndex) => (
              <th
                key={headerIndex}
                style={{ textAlign: 'left', padding: '10px', border: '1px solid #ccc' }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.rows.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`} style={{ border: '1px solid #ccc' }}>
              <td style={{ padding: '10px', border: '1px solid #ccc' }}>
                <label
                  className="row-label"
                  style={{ fontWeight: 'bold', marginRight: '1rem', flex: '0 0 auto' }}
                  htmlFor={`input-${rowIndex}`}
                >
                  {row.label}
                </label>
              </td>
              {row.inputs.map((input, inputIndex) => (
                <td
                  key={`input-${rowIndex}-${inputIndex}`}
                  style={{ padding: '10px', border: '1px solid #ccc' }}
                >
                  <input
                    {...props}
                    {...hooksFormRegister}
                    id={`input-${rowIndex}-${props.name}-${inputIndex}`}
                    type="text"
                    className="govuk-input govuk-!-width-100"
                    inputMode="text"
                    name={input.name} // Ensure each input has a unique name
                    value={formData[input.name]} // Bind to the individual field in state
                    onChange={handleInputChange}
                    onFocus={(e) =>
                      e.target.addEventListener('wheel', (e) => e.preventDefault(), {
                        passive: false,
                      })
                    }
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RowsWithInputs;
